import { type ReactNode } from 'react';
import { Navigate, Route } from 'react-router-dom';

import { ConceptTag } from '@/designs-dev/loft/conceptTag';
import {
  PROJECTS,
  conceptNumber,
  handoverPath,
  redirects,
  screenPath,
  variants,
} from '@/designs-dev/loft/projects';
import ScreenChrome from '@/designs-dev/loft/ScreenChrome';
import {
  HANDOVER_ELEMENTS,
  SCREEN_ELEMENTS,
} from '@/designs-dev/loft/elements';

// Routes, generated from the manifest rather than listed by hand.
//
// There are no navigation pages in here: an area page and a project page used to
// sit between the landing page and a prototype, and both were pages whose only
// job was to list links. Every route below renders a prototype or a spec.
//
// The master prototype is absent too. It is the member app mounted whole, under
// its own splat in App.tsx, so it keeps one mount and navigates internally.
// A concept's page, told which concept it is for its breadcrumb.
const asConcept = (n: number | undefined, element: ReactNode) =>
  n ? (
    <ConceptTag.Provider value={`Concept ${n}`}>{element}</ConceptTag.Provider>
  ) : (
    element
  );

export const loftRoutes = () => {
  const out = [];

  for (const p of PROJECTS) {
    if (p.kind === 'master') continue;

    for (const v of p.versions) {
      for (const s of v.screens) {
        // A cross-link points at a screen another project owns, so it is not a
        // route: the owner registers it.
        if (s.canonical) continue;
        const path = screenPath(p, v, s);
        // Keyed by the path it used to live at, or by where it lives now when
        // it never lived anywhere else. Without the fallback a new screen could
        // not be added at all, only a moved one.
        const element = SCREEN_ELEMENTS[s.legacy ?? path];
        if (!element) continue;
        // A screen that is one of several takes on the same thing, or that has
        // something to say about itself, gets the chrome strip above it.
        const siblings = variants(p, v, s);
        const wrapped =
          s.note || siblings.length > 1 ? (
            <ScreenChrome
              screen={s}
              siblings={siblings}
              pathOf={(sib) => screenPath(p, v, sib)}
            >
              {element}
            </ScreenChrome>
          ) : (
            element
          );
        out.push(
          <Route
            key={path}
            path={s.nested ? `${path}/*` : path}
            element={asConcept(
              conceptNumber(p.slug, 'screens', s.slug),
              wrapped,
            )}
          />,
        );
      }
    }

    for (const id of p.handovers ?? []) {
      const element = HANDOVER_ELEMENTS[id];
      if (!element) continue;
      const path = handoverPath(p, id);
      out.push(
        <Route
          key={path}
          path={path}
          element={asConcept(conceptNumber(p.slug, 'flows', id), element)}
        />,
      );
    }
  }

  return out;
};

// Every path that has ever been shared, pointed at its new home. Links have gone
// out in Slack and in Linear tickets, so an old one landing on the catch-all
// would look like the prototype was deleted.
export const loftRedirects = () =>
  Object.entries(redirects()).map(([from, to]) => (
    <Route key={from} path={from} element={<Navigate to={to} replace />} />
  ));
